
import { Component, Inject, Ref, Vue, Watch } from "vue-property-decorator";
import OrganizationTable from "../molecules/explorer-tool/OrganizationTable.vue";
import { Container } from "inversify";
// import { handleError } from "../../../common/src/utility/errorUtils";
import { OrganizationListStore } from "../stores/organization/OrganizationListStore";
import { OrganizationCollectionStore } from "../stores/geographic-tool/OrganizationCollectionStore";
import { OrganizationListItem } from "@gsx/common/src/types/http/dashboard/organization";
import FilterContainer from "../molecules/organization/FilterContainer.vue";
import CountryFilter from "../molecules/organization/CountryFilter.vue";
import { Coordinates, hasEnterpriseSubscription } from "@gsx/common";
import L, { LatLng, latLng, Point } from "leaflet";
import { LMap, LMarker, LTileLayer, LTooltip, LControl } from "vue2-leaflet";
import SubscriptionUpgrade from "../molecules/organization/views/SubscriptionUpgrade.vue";
import { AuthStore } from "../../../common/src/stores/AuthStore";
import { SubscriptionQuoteModalStore } from "../../../common/src/subscription-quote/SubscriptionQuoteModalStore";
import { handleError } from "../../../common/src/utility/errorUtils";
import { debounce } from "debounce";
import { OrganizationFilterStore } from "../stores/organization/OrganizationFilterStore";

@Component({
    components: {
        CountryFilter,
        FilterContainer,
        OrganizationTable,
        LMap,
        LTileLayer,
        LMarker,
        LTooltip,
        LControl,
        SubscriptionUpgrade,
    },
})
export default class GeographicAnalyticsView extends Vue {
    @Inject("container") readonly container!: Container;
    @Ref("map") map!: LMap;
    authStore: AuthStore = this.container.get(AuthStore);
    organizationMapCoordinatesListStore: OrganizationListStore = this.container.get(
        "OrganizationMapCoordinatesListStore",
    );
    organizationCollectionStore: OrganizationCollectionStore = this.container.get(
        OrganizationCollectionStore,
    );
    readonly subscriptionQuoteModalStore: SubscriptionQuoteModalStore = this.container.get(
        SubscriptionQuoteModalStore,
    );
    organizationFilterStore: OrganizationFilterStore = this.container.get(OrganizationFilterStore);

    altLocToggle = false;
    hqToggle = true;

    hadData = false;
    p = new Point(-5, -40);
    zoom = 4;

    created() {
        this.organizationMapCoordinatesListStore.onlyCoordinates = true;
        this.organizationMapCoordinatesListStore.load().catch(handleError);
    }

    get organizations(): OrganizationListItem[] {
        return this.organizationMapCoordinatesListStore.items || [];
    }

    get hasEnterpriseSubscription() {
        return hasEnterpriseSubscription(this.authStore.user);
    }

    get markers() {
        // @ts-ignore
        this.depRender = this.zoom;

        const HQOrgColor = "black";
        const AltOrgColor = "green";
        let markers = this.organizations
            .filter(
                (org) => org.coordinates && org.coordinates.latitude && org.coordinates.longitude,
            )
            .map((org: OrganizationListItem) => {
                let orgMarkers: {
                    latLng: LatLng;
                    icon: any;
                    tooltip: string;
                    id: string;
                    visible: boolean;
                }[] = [];

                if (org.addresses) {
                    orgMarkers = org.addresses
                        .filter(
                            (addWithCoord) =>
                                addWithCoord.coordinates &&
                                addWithCoord.coordinates.latitude &&
                                addWithCoord.coordinates.longitude,
                        )
                        .map((address, index) =>
                            this.createMarker(
                                org,
                                address.coordinates!,
                                `${org.organizationid}##${index}`,
                                AltOrgColor,
                            ),
                        )
                        .map((marker) => ({ ...marker, visible: this.altLocToggle }));
                }

                if (org.coordinates) {
                    let hqOrg = this.createMarker(
                        org,
                        org.coordinates!,
                        `${org.organizationid}##${-1}`,
                        HQOrgColor,
                    );
                    hqOrg.visible = this.hqToggle;
                    orgMarkers.push(hqOrg);
                }

                return orgMarkers;
            })
            .flat();

        if (markers.length) {
            this.hadData = true;
        }

        return markers;
    }

    reload() {
        this.organizationMapCoordinatesListStore.reload().catch(handleError);
    }

    @Watch("organizationFilterStore.serviceTags")
    @Watch("organizationFilterStore.productTags")
    @Watch("organizationFilterStore.operatingRegimeTags")
    @Watch("organizationFilterStore.applicationTags")
    @Watch("organizationFilterStore.countries")
    @Watch("organizationFilterStore.states")
    @Watch("organizationFilterStore.logicalOperator")
    @Watch("organizationFilterStore.afterYear")
    @Watch("organizationFilterStore.beforeYear")
    reloadDebounce = debounce(this.reload, 300);

    zoomUpdated(val: any) {
        this.zoom = val;
    }

    createMarker(
        organization: OrganizationListItem,
        coordinates: Coordinates,
        id: string,
        color: string = "black",
    ): { latLng: LatLng; icon: any; tooltip: string; id: string; visible: boolean } {
        const markerHtmlStyles = `
            background-color: ${color};
            width: 1rem;
            height: 1rem;
            display: block;
            left: 0rem;
            top: 0rem;
            position: relative;
            border-radius: 3rem 3rem 0;
            transform: rotate(45deg);
            border: 1px solid #FFFFFF`;

        const icon = L.divIcon({
            className: "my-custom-pin",
            iconAnchor: [8, 24],
            popupAnchor: [0, -36],
            html: `<span style="${markerHtmlStyles}" />`,
        });

        return {
            latLng: latLng(coordinates!.latitude, coordinates!.longitude),
            icon: icon,
            tooltip: hasEnterpriseSubscription(this.authStore.user)
                ? organization.organizationName
                : "Upgrade for more",
            id: id,
            visible: true,
        };
    }

    onMarkerClick(id: string) {
        if (hasEnterpriseSubscription(this.authStore.user)) {
            const parsedId = id.split("##")[0];
            const routeData = this.$router.resolve(`/organizations/${parsedId}`);
            window.open(routeData.href, "_blank");
        } else {
            this.subscriptionQuoteModalStore.open();
        }
    }
}
